import React from 'react'
import { Button, Card, Form, InputNumber, Modal, Popconfirm, Select, Space, Table, Typography, message } from 'antd'
import { listShops } from '../../api/shops'
import { listDeliveryLocations } from '../../api/deliveryLocations'
import { listShopLocationFees, createShopLocationFee, updateShopLocationFee, deleteShopLocationFee } from '../../api/shopLocationFees'

export default function ShopLocationFeesAdmin() {
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  const [shops, setShops] = React.useState([])
  const [locations, setLocations] = React.useState([])

  const [createOpen, setCreateOpen] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const [editOpen, setEditOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [selected, setSelected] = React.useState(null)

  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()

  const fetchData = React.useCallback(async () => {
    setLoading(true)
    try {
      const [fees, shopsList, locationsList] = await Promise.all([
        listShopLocationFees(),
        listShops(),
        listDeliveryLocations(),
      ])
      setData(fees || [])
      setShops(shopsList || [])
      setLocations(locationsList || [])
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { fetchData() }, [fetchData])

  function parseError(e) {
    const t = (e && e.message) ? e.message : 'Request failed'
    return t.replace(/^[\s"']+|[\s"']+$/g, '')
  }

  async function onCreate(values) {
    setCreating(true)
    try {
      const payload = {
        shopId: values.shopId,
        locationId: values.locationId,
        fee: values.fee == null ? null : String(values.fee),
      }
      await createShopLocationFee(payload)
      message.success('تم الإنشاء')
      setCreateOpen(false)
      createForm.resetFields()
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setCreating(false)
    }
  }

  function openEdit(record) {
    setSelected(record)
    editForm.setFieldsValue({
      shopId: record.shop?.id,
      locationId: record.location?.id,
      fee: record.fee != null ? Number(record.fee) : null,
    })
    setEditOpen(true)
  }

  async function onEdit(values) {
    if (!selected) return
    setEditing(true)
    try {
      const selFee = selected.fee == null ? null : Number(selected.fee)
      const newFee = values.fee == null ? null : Number(values.fee)
      const payload = {}
      if (selFee !== newFee) payload.fee = values.fee == null ? null : String(values.fee)

      if (Object.keys(payload).length === 0) {
        message.info('لا تغييرات للحفظ')
      } else {
        await updateShopLocationFee(selected.shop?.id, selected.location?.id, payload)
        message.success('تم التحديث')
      }
      setEditOpen(false)
      setSelected(null)
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setEditing(false)
    }
  }

  async function onDelete(shopId, locationId) {
    try {
      await deleteShopLocationFee(shopId, locationId)
      message.success('تم الحذف')
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    }
  }

  const columns = [
    { title: 'المتجر', key: 'shop', render: (_, r) => r?.shop?.nameAr || '' },
    { title: 'المنطقة', key: 'location', render: (_, r) => r?.location?.nameAr || '' },
    { title: 'الرسوم', dataIndex: 'fee', key: 'fee', width: 120 },
    {
      title: '', key: 'actions', width: 220,
      render: (_, record) => (
        <Space>
          <Button onClick={() => openEdit(record)}>تعديل</Button>
          <Popconfirm title="حذف هذا الصف؟" onConfirm={() => onDelete(record.shop?.id, record.location?.id)}>
            <Button danger>حذف</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{maxWidth: 1200, margin: '40px auto', padding: '0 16px'}}>
      <Space style={{display: 'flex', justifyContent: 'space-between', marginBottom: 16}}>
        <Typography.Title level={3} style={{margin: 0}}>رسوم التوصيل: متجر ← منطقة</Typography.Title>
        <Button type="primary" onClick={() => setCreateOpen(true)}>سطر جديد</Button>
      </Space>
      <Card>
        <Table
          rowKey={(r) => `${r?.shop?.id}_${r?.location?.id}`}
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="إنشاء صف"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText="إنشاء"
        confirmLoading={creating}
        onOk={() => createForm.submit()}
      >
        <Form form={createForm} layout="vertical" onFinish={onCreate}>
          <Form.Item label="المتجر" name="shopId" rules={[{required: true, message: 'يرجى اختيار متجر'}]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={shops.map(s => ({ label: s.nameAr || s.name, value: s.id }))}
            />
          </Form.Item>
          <Form.Item label="منطقة التوصيل" name="locationId" rules={[{required: true, message: 'يرجى اختيار منطقة'}]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={locations.map(l => ({ label: l.nameAr || l.name, value: l.id }))}
            />
          </Form.Item>
          <Form.Item label="الرسوم" name="fee">
            <InputNumber min={0} step={0.01} style={{width: '100%'}} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="تعديل صف"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        okText="حفظ"
        confirmLoading={editing}
        onOk={() => editForm.submit()}
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item label="المتجر" name="shopId">
            <Select disabled showSearch optionFilterProp="label" options={shops.map(s => ({ label: s.nameAr || s.name, value: s.id }))} />
          </Form.Item>
          <Form.Item label="منطقة التوصيل" name="locationId">
            <Select disabled showSearch optionFilterProp="label" options={locations.map(l => ({ label: l.nameAr || l.name, value: l.id }))} />
          </Form.Item>
          <Form.Item label="الرسوم" name="fee">
            <InputNumber min={0} step={0.01} style={{width: '100%'}} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
